import { Injectable } from '@angular/core';
import type { HubConnection } from '@microsoft/signalr';
import { TokenService } from './token.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {

  private connection: HubConnection | null = null;

  constructor(
    private tokenService: TokenService,
    private notification: NzNotificationService
  ) {}

  async startConnection(): Promise<void> {
    const token = this.tokenService.getAccessToken();
    if (!token) {
      return;
    }

    // Request browser notification permission
    await this.requestNotificationPermission();

    try {


      const userJson = this.tokenService.getUser();
      var roles = this.tokenService.getRoles();


      if (userJson) {
        const user = JSON.parse(userJson);
        if (user?.id && roles.includes("SuperAdmin")) {

          // Loaded on demand so the SignalR client stays out of the initial
          // bundle; only SuperAdmin sessions ever download it.
          const signalR = await import('@microsoft/signalr');

          this.connection = new signalR.HubConnectionBuilder()
            .withUrl(environment.signalREndpoint, {
              accessTokenFactory: () => this.tokenService.getAccessToken() || ''
            })
            .withAutomaticReconnect()
            .build();

          this.registerListeners();

          await this.connection.start();
          console.log('SignalR connected');

          await this.connection.invoke('RegisterUser', user.id);
        }
      }
    } catch (err) {
      console.error('SignalR connection error:', err);
    }
  }

  async stopConnection(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      const userJson = this.tokenService.getUser();
      if (userJson) {
        const user = JSON.parse(userJson);
        if (user?.id) {
          await this.connection.invoke('UnregisterUser', user.id);
        }
      }
      await this.connection.stop();
      console.log('SignalR disconnected');
    } catch (err) {
      console.error('SignalR disconnect error:', err);
    } finally {
      this.connection = null;
    }
  }

  private async requestNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (err) {
        console.error('Error requesting notification permission:', err);
      }
    }
  }

  private showBrowserNotification(title: string, body: string): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body: body,
        icon: 'favicon.ico',
        badge: 'favicon.ico',
        tag: 'quotation-notification',
        requireInteraction: false
      });
    }
  }

  private registerListeners(): void {
    if (!this.connection) {
      return;
    }

    this.connection.on('NewQuotation', (data: any) => {
      console.log('New quotation:', data);

      // Show in-app notification
      this.notification.success('New Quotation', 'New quotation received');

      // Show browser push notification
      this.showBrowserNotification('New Quotation', 'New quotation received');
    });
  }
}
